import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import {
  useAccountSettings,
  useBalances,
  useHookForm,
  useMerchantApiMutation,
  useMerchantApiQuery,
  useMerchantApiQueryWithParams,
} from "../../hooks";
import {
  IPaymentMethodsSettings,
  ISwAppSupportedPaymentGateway,
  PagedMeta,
  IPageAndSearchRequest,
  IHookForm,
  IDisabledGateway,
  IDisabledPaymentMethod,
  ISwAppSupportedPaymentMethod,
  IBank,
  NoMeta,
  IDisabledDvaBank,
} from "../../interfaces-and-types";
import { globalHandlerUtils } from "../../utils";

import { View } from "./View";
import { appConstants } from "../../constants";

interface IPaymentMethodProps {
  hideActivationRequired?: boolean;
}

interface ISupportedBankDvaStatus extends IBank {
  is_enabled: boolean;
}

interface ISupportedGatewayStatus extends ISwAppSupportedPaymentGateway {
  is_enabled: boolean;
}

interface ISupportedPaymentMethodStatus extends ISwAppSupportedPaymentMethod {
  is_enabled: boolean;
}

export interface IPaymentMethodViewProps
  extends IHookForm<IPaymentMethodsSettings> {
  hideActivationRequired?: boolean;

  isLoadingToggleDvaBank: boolean;
  isLoadingToggleGateway: boolean;
  isLoadingTogglePaymentMethod: boolean;
  toggleGateway: (disabledGw: IDisabledGateway) => void;
  toggleDvaBank: (disabledBnk: IDisabledDvaBank) => void;
  togglePaymentMethod: (disabledPm: IDisabledPaymentMethod) => void;

  isLoadingSupportedDvaBanks: boolean;
  isLoadingSupportedGateways: boolean;
  isLoadingSupportedPaymentMethods: boolean;
  supportedDvaBanksStatusList: ISupportedBankDvaStatus[];
  supportedGatewaysStatusList: ISupportedGatewayStatus[];
  supportedPaymentMethods: ISupportedPaymentMethodStatus[];
  goBack: () => void;
  isDirty: boolean;
}

export const PaymentMethod = (props: IPaymentMethodProps) => {
  const navigate = useNavigate();
  const { activeBalanceCurrency } = useBalances();

  const {
    isSuccess: isSuccessAccountSettings,
    isLoading: isLoadingAccountSettings,
    data: accountSettings,
    refreshRemoteData: refreshAccountSettings,
  } = useAccountSettings();

  const {
    isLoading: isLoadingSupportedDvaBanks,
    // isSuccess: isSuccessSupportedDvaBanks,
    data: supportedDvaBanks,
  } = useMerchantApiQueryWithParams<
    {
      currency: string;
      country: string;
    },
    IBank[],
    NoMeta
  >(
    [
      "getPartnerBanks",
      {
        country: appConstants.DEFAULT_COUNTRY_STATIC, // FIX LATER
        currency: activeBalanceCurrency!,
      },
    ],
    {
      enabled:
        !!activeBalanceCurrency &&
        activeBalanceCurrency !== "" &&
        !!appConstants.DEFAULT_COUNTRY_STATIC, // FIX LATER
    }
  );

  const {
    isLoading: isLoadingSupportedGateways,
    isSuccess: isSuccessSupportedGateways,
    data: supportedGatewaysPage,
    // meta: productsMeta,
  } = useMerchantApiQueryWithParams<
    IPageAndSearchRequest,
    ISwAppSupportedPaymentGateway[],
    PagedMeta
  >(
    [
      "getSupportedGateways",
      {
        page: 1,
        page_size: 100,
        search: "",
      },
    ],
    {
      enabled: isSuccessAccountSettings,
    }
  );

  const {
    isLoading: isLoadingSupportedPaymentChannels,
    isSuccess: isSuccessSupportedPaymentChannels,
    data: supportedPaymentChannelsPage,
    // meta: productsMeta,
  } = useMerchantApiQueryWithParams<IPageAndSearchRequest, string[], PagedMeta>(
    [
      "getSupportedPaymentChannels",
      {
        page: 1,
        page_size: 100,
        search: "",
      },
    ],
    {
      enabled: isSuccessAccountSettings,
    }
  );

  const enabledSupportedDvaBanksToDisplay: ISupportedBankDvaStatus[] =
    isLoadingSupportedDvaBanks || !supportedDvaBanks
      ? []
      : supportedDvaBanks?.map((bnk) => ({
          ...bnk,
          is_enabled:
            accountSettings?.disabled_dva_banks.find(
              (disabledBnk) => disabledBnk.bank_code === bnk.bank_code
            ) === undefined,
        }));

  const enabledSupportedGatewaysToDisplay: ISupportedGatewayStatus[] =
    isLoadingSupportedGateways || !supportedGatewaysPage
      ? []
      : supportedGatewaysPage?.map((gw) => ({
          ...gw,
          is_enabled:
            accountSettings?.disabled_gateways.find(
              (disabledGw) => disabledGw.gateway_code === gw.gateway_code
            ) === undefined,
        }));

  const enabledSupportedPaymentChannelsToDisplay: ISupportedPaymentMethodStatus[] =
    isLoadingSupportedPaymentChannels || !supportedPaymentChannelsPage
      ? []
      : supportedPaymentChannelsPage?.map((payMeth) => ({
          payment_method: payMeth,
          is_supported: true,
          is_enabled:
            accountSettings?.disabled_payment_methods.find(
              (disabledPm) => disabledPm.payment_method === payMeth
            ) === undefined,
        }));

  const initialFormValues: IPaymentMethodsSettings = {
    enable_newly_supported_payment_methods: false,
    enable_newly_supported_gateways: false,
    enable_newly_supported_countries: false,
    enable_newly_supported_currencies: false,
    global_enabled_payment_methods: [],
    global_enabled_dva_banks: [],
    global_enabled_gateways: [],
    global_enabled_currencies: [],
    global_enabled_countries: [],

    // ...accountSettings,
  };

  const {
    isLoading: isLoadingToggleDvaBank,
    isSuccess: isSuccessToggleDvaBank,
    mutate: toggleDvaBank,
  } = useMerchantApiMutation<IDisabledDvaBank, IPaymentMethodsSettings>(
    "toggleDvaBankSetting"
  ).usePutMutation;

  const {
    isLoading: isLoadingToggleGateway,
    isSuccess: isSuccessToggleGateway,
    mutate: toggleGateway,
  } = useMerchantApiMutation<IDisabledGateway, IPaymentMethodsSettings>(
    "toggleGatewaySetting"
  ).usePutMutation;

  const {
    isLoading: isLoadingTogglePaymentMethod,
    isSuccess: isSuccessTogglePaymentMethod,
    mutate: togglePaymentMethod,
  } = useMerchantApiMutation<IDisabledPaymentMethod, IPaymentMethodsSettings>(
    "togglePaymentMethodSetting"
  ).usePutMutation;

  useEffect(() => {
    if (isSuccessToggleGateway || isSuccessTogglePaymentMethod) {
      refreshAccountSettings();
    }
  }, [isSuccessToggleGateway, isSuccessTogglePaymentMethod]);

  const handleApiSubmit = (data: IPaymentMethodsSettings) => {
    // the form is a dummy used only for checking isDirty status
    // use the toggle functions instead
  };

  const handleError = (errors: any) => globalHandlerUtils.handleError(errors);

  const { onFormSubmit, isDirty, errors, register, control, reset, watch } =
    useHookForm({
      initialFormValues,
      validationSchema: undefined,
      onValid: handleApiSubmit,
      onInvalid: handleError,
    });

  useEffect(() => {
    if (accountSettings) reset(accountSettings); // update the settings
  }, [isLoadingAccountSettings]);

  const goBack = () => navigate("/settings");

  return (
    <View
      hideActivationRequired={props.hideActivationRequired}
      isLoadingSupportedDvaBanks={isLoadingSupportedDvaBanks}
      isLoadingSupportedGateways={isLoadingSupportedGateways}
      isLoadingSupportedPaymentMethods={isLoadingSupportedPaymentChannels}
      supportedDvaBanksStatusList={enabledSupportedDvaBanksToDisplay || []}
      supportedGatewaysStatusList={enabledSupportedGatewaysToDisplay || []}
      supportedPaymentMethods={enabledSupportedPaymentChannelsToDisplay || []}
      isLoadingToggleDvaBank={isLoadingToggleDvaBank}
      isLoadingToggleGateway={isLoadingToggleGateway}
      isLoadingTogglePaymentMethod={isLoadingTogglePaymentMethod}
      toggleDvaBank={toggleDvaBank}
      toggleGateway={toggleGateway}
      togglePaymentMethod={togglePaymentMethod}
      goBack={goBack}
      isDirty={isDirty}
      handleSubmit={onFormSubmit}
      register={register}
      errors={errors}
    />
  );
};
